import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import ArkHeader from "@/components/ArkHeader";
import ArkFooter from "@/components/ArkFooter";
import { EditableText, EditableImage } from "@/components/Editable";
import { CATEGORIES, categoryName } from "@/lib/categories";
import NotifySection from "@/components/NotifySection";
import CategorySilhouette from "@/components/CategorySilhouette";
import { trackOnce } from "@/lib/tracking";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import ShareCoupon from "@/components/ShareCoupon";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f592387a-49b8-4dc7-ab15-a739df15176f/images/7ff1c8af8ee42e21ee96e6cdd66f2daef2437db6533753dce9560b696e8fb5fe.png";

const CRAFT_IMG =
  "https://images.pexels.com/photos/5095280/pexels-photo-5095280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState(null); // slug or null
  const [transformations, setTransformations] = useState([]);

  useEffect(() => {
    trackOnce("/");
    api
      .get("/furniture")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    api
      .get("/transformations")
      .then((r) => setTransformations(r.data))
      .catch(() => setTransformations([]));
  }, []);

  // Scroll to an anchor in the URL (e.g. /#recent-transformations) once the
  // sections have actually mounted — handles deep links from the header Gallery button
  useEffect(() => {
    if (loading) return; // wait until pieces/transformations are loaded so the section exists
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      // Tiny timeout so React has flushed the new DOM before we scroll
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [loading, transformations]);

  // Count items per category
  const counts = useMemo(() => {
    const m = {};
    for (const it of items) {
      const c = it.category || "uncategorised";
      m[c] = (m[c] || 0) + 1;
    }
    return m;
  }, [items]);

  const filtered = useMemo(() => {
    if (!selectedCat) return items;
    return items.filter((it) => it.category === selectedCat);
  }, [items, selectedCat]);

  const newThisWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return items.filter((it) => {
      const t = it.created_at ? new Date(it.created_at).getTime() : 0;
      return t >= cutoff;
    });
  }, [items]);

  const pickCategory = (slug) => {
    setSelectedCat(slug);
    // Scroll to gallery
    setTimeout(() => {
      const el = document.getElementById("collection");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div data-testid="home-page" className="bg-white text-black">
      <ArkHeader />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-[var(--ark-border)]"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 px-6 md:px-10 pt-16 md:pt-24 pb-20">
          <div className="lg:col-span-7 flex flex-col justify-center reveal">
            <EditableText
              contentKey="hero_headline_1"
              fallback="Old Wood,"
              as="h1"
              className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight"
            >
              {(v) => (
                <>
                  {v}
                  <br />
                  <EditableText
                    contentKey="hero_headline_2"
                    fallback="New Life."
                    as="span"
                    className="inline-block"
                  />
                  <br />
                  <EditableText
                    contentKey="hero_headline_3"
                    fallback="Finished Your Way."
                    as="span"
                    className="italic font-normal text-[var(--ark-accent)] inline-block"
                  />
                </>
              )}
            </EditableText>

            <EditableText
              contentKey="hero_body"
              fallback="Every piece is sourced, restored, and hand-finished to your taste. Pick a type, choose your finish, and we'll build it just for you."
              as="p"
              multiline
              className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[var(--ark-muted)]"
            />

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#categories"
                data-testid="hero-browse-btn"
                className="ark-btn !text-sm md:!text-base !px-8 md:!px-10 !py-4 md:!py-5 !tracking-[0.22em]"
              >
                <EditableText contentKey="hero_cta_1" fallback="Start customizing" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <EditableImage
              contentKey="hero_image"
              fallback={HERO_IMG}
              aspect={4 / 5}
              alt="Restored wood furniture detail"
              className="ark-grain relative aspect-[4/5] w-full overflow-hidden"
              imgClassName="h-full w-full object-cover"
            />
            <div
              className="hidden md:block absolute -bottom-6 -left-6 bg-[var(--ark-accent)] text-white px-5 py-4"
              style={{ maxWidth: "240px" }}
            >
              <EditableText
                contentKey="hero_tag_title"
                fallback="No two pieces alike."
                as="div"
                className="font-display text-xl leading-tight"
              />
              <EditableText
                contentKey="hero_tag_sub"
                fallback="One-of-one restorations"
                as="div"
                className="text-[0.65rem] uppercase tracking-[0.2em] mt-1 opacity-90"
              />
            </div>
          </div>
        </div>
      </section>

      {/* NEW THIS WEEK */}
      {!selectedCat && newThisWeek.length > 0 && (
        <section
          data-testid="new-this-week"
          className="bg-black text-white"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div className="reveal">
                <span className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
                  <Sparkles size={12} /> Just landed
                </span>
                <EditableText
                  contentKey="new_heading"
                  fallback="Just sourced this week"
                  as="h2"
                  className="font-display text-3xl md:text-4xl tracking-tight mt-2 max-w-2xl"
                />
              </div>
              <span className="text-xs uppercase tracking-[0.18em] text-white/60">
                {newThisWeek.length} new {newThisWeek.length === 1 ? "piece" : "pieces"}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {newThisWeek.slice(0, 8).map((item, idx) => (
                <Link
                  key={item.id}
                  to={`/piece/${item.id}`}
                  data-testid={`new-card-${item.id}`}
                  className="ark-card group block reveal"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-white/5 relative">
                    <img
                      src={resolveImage(item.image_url)}
                      alt={item.title}
                      className={`h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06] ${item.is_sold ? "opacity-70" : ""}`}
                    />
                    {item.is_sold ? (
                      <span className="absolute top-3 left-3 bg-white text-black text-[0.55rem] uppercase tracking-[0.18em] px-2 py-1">
                        Sold
                      </span>
                    ) : (
                      <span className="absolute top-3 left-3 bg-[var(--ark-accent)] text-white text-[0.55rem] uppercase tracking-[0.18em] px-2 py-1">
                        New
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.category && (
                        <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--ark-accent)]">
                          {categoryName(item.category)}
                        </span>
                      )}
                      {item.code && (
                        <span className="font-mono text-[0.55rem] tracking-[0.18em] text-white/60">
                          {item.code}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-lg leading-tight mt-1 group-hover:text-[var(--ark-accent)] transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CATEGORY PICKER */}
      <section
        id="categories"
        data-testid="category-picker"
        className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10"
      >
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div className="reveal">
            <EditableText
              contentKey="categories_eyebrow"
              fallback="Start here"
              as="span"
              className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]"
            />
            <EditableText
              contentKey="categories_heading"
              fallback="What piece are you dreaming of?"
              as="h2"
              className="font-display text-4xl md:text-6xl tracking-tight mt-2 leading-[1]"
            />
          </div>
          <EditableText
            contentKey="categories_note"
            fallback="Pick a type to see what we have right now."
            as="p"
            multiline
            className="text-sm text-[var(--ark-muted)] max-w-sm reveal"
            style={{ animationDelay: "120ms" }}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {CATEGORIES.map((c, i) => {
            const count = counts[c.slug] || 0;
            const has = count > 0;
            const active = selectedCat === c.slug;
            return (
              <button
                key={c.slug}
                type="button"
                data-testid={`cat-${c.slug}`}
                onClick={() => pickCategory(c.slug)}
                style={{ animationDelay: `${i * 40}ms` }}
                className={`cat-tile reveal group relative aspect-square border overflow-hidden p-4 flex flex-col items-stretch justify-between text-left transition-all duration-300 ${
                  active
                    ? "border-[var(--ark-accent)] bg-black text-white"
                    : has
                      ? "border-[var(--ark-border)] hover:border-[var(--ark-accent)] hover:bg-[var(--ark-bone)]"
                      : "border-[var(--ark-border)] opacity-70 hover:opacity-100 hover:border-black"
                }`}
              >
                <span
                  className={`inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] ${
                    active
                      ? "text-[var(--ark-accent)]"
                      : has
                        ? "text-[var(--ark-accent)]"
                        : "text-[var(--ark-muted)]"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${has ? "bg-[var(--ark-accent)]" : "bg-[var(--ark-muted)]"} ${has ? "animate-pulse" : ""}`}
                  />
                  {has ? `${count} available` : "none right now"}
                </span>
                <div className="flex-1 flex items-center justify-center py-1 pointer-events-none">
                  <CategorySilhouette
                    slug={c.slug}
                    className={`w-[60%] h-[60%] transition-all duration-300 ${
                      active
                        ? "text-[var(--ark-accent)]"
                        : has
                          ? "text-black/70 group-hover:text-[var(--ark-accent)]"
                          : "text-[var(--ark-muted)] group-hover:text-black/70"
                    } group-hover:scale-105`}
                  />
                </div>
                <div className="w-full">
                  <span className="block font-display text-xl md:text-2xl leading-tight transition-transform duration-300 group-hover:-translate-y-0.5">
                    {c.name}
                  </span>
                  <span
                    className={`mt-2 block h-px transition-all duration-300 ${
                      active
                        ? "w-full bg-[var(--ark-accent)]"
                        : "w-0 bg-[var(--ark-accent)] group-hover:w-full"
                    }`}
                  />
                </div>
                {active && (
                  <span className="absolute top-3 right-3 text-[var(--ark-accent)]">
                    <Check size={16} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* SHARE & 10% OFF */}
      <ShareCoupon />

      {/* RECENT TRANSFORMATIONS */}
      {transformations.length > 0 && (
        <section
          id="recent-transformations"
          data-testid="recent-transformations"
          className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-20 md:pb-24"
        >
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20 reveal">
            <span className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]">
              Our work
            </span>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-3 leading-[1.05]">
              Recent Transformations
            </h2>
            <p className="text-sm md:text-base text-[var(--ark-muted)] mt-5 leading-relaxed">
              A look at pieces we&apos;ve recently brought back to life. Each
              one hand-finished in our workshop.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {transformations.map((t, i) => (
              <figure
                key={t.id}
                data-testid={`transformation-${t.id}`}
                style={{ animationDelay: `${i * 60}ms` }}
                className="reveal group"
              >
                <div className="aspect-[4/5] overflow-hidden bg-[var(--ark-bone)] rounded-2xl">
                  <img
                    src={resolveImage(t.image_url)}
                    alt={t.label || "Finished piece"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
                {t.label && (
                  <figcaption className="mt-4 text-center text-[0.7rem] md:text-[0.75rem] uppercase tracking-[0.22em] text-[var(--ark-muted)]">
                    {t.label}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>

          <div className="mt-14 md:mt-20 flex justify-center reveal">
            <a
              href="#categories"
              data-testid="transformations-cta"
              className="ark-btn !text-sm md:!text-base !px-10 md:!px-12 !py-4 md:!py-5 !tracking-[0.22em]"
            >
              Start Customizing
            </a>
          </div>
        </section>
      )}

      {/* COLLECTION */}
      <section
        id="collection"
        data-testid="collection-section"
        className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-24"
      >
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <EditableText
              contentKey="collection_eyebrow"
              fallback={selectedCat ? "Step two" : "The full collection"}
              as="span"
              className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]"
            />
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-2">
              {selectedCat ? (
                <>
                  Pick your{" "}
                  <span className="italic text-[var(--ark-accent)]">
                    {categoryName(selectedCat).toLowerCase()}
                  </span>
                  <span className="text-[var(--ark-muted)] font-normal text-2xl md:text-3xl">
                    {" "}
                    · {filtered.length}{" "}
                    {filtered.length === 1 ? "piece" : "pieces"}
                  </span>
                </>
              ) : (
                <EditableText
                  contentKey="collection_heading"
                  fallback="Every piece, ready for its next life"
                  as="span"
                />
              )}
            </h2>
          </div>
          {selectedCat ? (
            <button
              type="button"
              data-testid="cat-clear"
              onClick={() => setSelectedCat(null)}
              className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] hover:text-[var(--ark-accent)] transition-colors"
            >
              <ArrowLeft size={14} /> Choose a different type
            </button>
          ) : (
            <EditableText
              contentKey="collection_note"
              fallback="Tap a piece to see more, then design your finish."
              as="p"
              multiline
              className="text-sm text-[var(--ark-muted)] max-w-sm"
            />
          )}
        </div>

        {loading ? (
          <div
            data-testid="collection-loading"
            className="py-20 text-center text-[var(--ark-muted)]"
          >
            Loading the workshop…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="collection-empty"
            className="py-20 text-center border border-dashed border-[var(--ark-border)]"
          >
            <p className="font-display text-2xl">
              {selectedCat
                ? `No ${categoryName(selectedCat).toLowerCase()}s right now.`
                : "Nothing listed yet."}
            </p>
            <p className="text-sm text-[var(--ark-muted)] mt-2 max-w-md mx-auto">
              New pieces go up weekly. Sign up below to be first to know when
              one arrives.
            </p>
            {selectedCat && (
              <button
                type="button"
                onClick={() => setSelectedCat(null)}
                className="ark-btn ark-btn-ghost mt-6"
              >
                Try another type
              </button>
            )}
          </div>
        ) : (
          <div
            key={selectedCat || "all"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12"
          >
            {filtered.map((item, idx) => (
              <Link
                key={item.id}
                to={`/piece/${item.id}`}
                data-testid={`furniture-card-${item.id}`}
                className="ark-card group block reveal"
                style={{ animationDelay: `${idx * 90}ms` }}
              >
                <div className="ark-grain aspect-[4/5] overflow-hidden bg-[var(--ark-bone)] relative">
                  <img
                    src={resolveImage(item.image_url)}
                    alt={item.title}
                    className={`h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06] ${item.is_sold ? "opacity-70" : ""}`}
                  />
                  {item.is_sold && (
                    <div
                      data-testid={`sold-badge-${item.id}`}
                      className="absolute top-4 left-4 bg-black text-white text-[0.6rem] uppercase tracking-[0.22em] px-2.5 py-1"
                    >
                      Sold
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-5 left-5 right-5 text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="inline-flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.22em] bg-[var(--ark-accent)] px-3 py-1.5">
                      {item.is_sold ? "View this piece" : "Design your finish"}
                      <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.category && (
                      <span className="text-[0.62rem] uppercase tracking-[0.2em] text-[var(--ark-accent)]">
                        {categoryName(item.category)}
                      </span>
                    )}
                    {item.code && (
                      <span
                        data-testid={`card-code-${item.id}`}
                        className="font-mono text-[0.55rem] tracking-[0.18em] text-[var(--ark-muted)]"
                      >
                        {item.code}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mt-1 inline-block">
                    <span className="relative">
                      {item.title}
                      <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-[var(--ark-accent)] transition-all duration-500 group-hover:w-full" />
                    </span>
                  </h3>
                  {/* Price intentionally hidden on cards — only revealed on the final wizard step */}
                  {item.dimensions && (
                    <p className="text-[0.72rem] uppercase tracking-[0.18em] text-[var(--ark-muted)] mt-1.5">
                      {item.dimensions}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* NOTIFY SIGNUP */}
      <NotifySection />

      {/* CRAFT BAND */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <EditableImage
              contentKey="craft_image"
              fallback={CRAFT_IMG}
              aspect={5 / 4}
              alt="Hands finishing wood"
              className="ark-grain aspect-[5/4] overflow-hidden relative"
              imgClassName="h-full w-full object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <EditableText
              contentKey="craft_eyebrow"
              fallback="Our Promise"
              as="span"
              className="text-[0.72rem] uppercase tracking-[0.28em] text-[var(--ark-accent)]"
            />
            <EditableText
              contentKey="craft_heading_1"
              fallback="Every grain. Every joint."
              as="h2"
              className="font-display text-3xl md:text-5xl tracking-tight mt-3"
            >
              {(v) => (
                <>
                  {v} <br />
                  <EditableText
                    contentKey="craft_heading_2"
                    fallback="Finished your way."
                    as="span"
                    className="italic text-[var(--ark-accent)]"
                  />
                </>
              )}
            </EditableText>
            <EditableText
              contentKey="craft_body"
              fallback="Tell us the type of piece you'd love, we'll source it, restore it, and finish it your way. No two ARK pieces are ever the same."
              as="p"
              multiline
              className="mt-6 text-white/75 leading-relaxed max-w-xl"
            />
            <a
              href="#categories"
              className="ark-btn mt-8 !bg-white !text-black !border-white hover:!bg-[var(--ark-accent)] hover:!text-white hover:!border-[var(--ark-accent)]"
              data-testid="craft-cta"
            >
              <EditableText contentKey="craft_cta" fallback="Start customizing" />
            </a>
          </div>
        </div>
      </section>

      <ArkFooter />
    </div>
  );
}
